import type { ThemeContext } from './themeContext';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { ThemeDriver } from './engine/driver';
import type { FlowThemeProgress, SceneAnchor, SceneFactory } from './engine/types';
import type { FlowThemeDefinition } from './definition';
import type { PerformanceFallbackReport } from './flowThemeAnalytics';
import { backingPixels, fitPixelRatio, FrameBudget, MAX_PIXEL_RATIO, type FrameCostStats } from './renderBudget';
import { readRenderMemory, rememberPixelBudget, rememberSlowDevice } from './renderEnvironment';

const COMPACT_BREAKPOINT = 768;
// Resizes are applied once the size has held still this long. Meanwhile the old
// frame stretches to fit, so a window drag doesn't rebuild scene caches every frame.
const RESIZE_SETTLE_MS = 160;
const INTERACTIVE_SELECTOR = '[data-flow-surface], button, a, input, textarea, select, label, [role="button"], [role="dialog"]';
/** Once the motion settles (and nothing changed for a moment), draw at 30fps. */
const SETTLED_AFTER_MS = 2500;
const SETTLED_FRAME_MS = 1000 / 30;
/**
 * Settled this long with no step or tap, only ambient motion is left: draw at
 * 15fps. Each frame may then step the scene clock a full frame, so ambient
 * motion keeps its real-time speed, just with fewer frames.
 */
const IDLE_AFTER_MS = 10_000;
const IDLE_FRAME_MS = 1000 / 15;
const IDLE_MAX_DELTA_SECONDS = 1 / 12;
/** Cross-fade from the last animated frame to the still one when frames go still for performance. */
const STILL_CROSSFADE_MS = 450;
/** Chrome may throttle an occluded window to one callback per second without hiding its document. */
const SUSPENDED_FRAME_GAP_MS = 750;

export interface FlowThemeCanvasProps {
  theme: FlowThemeDefinition;
  themeContext?: ThemeContext;
  progress: FlowThemeProgress;
  /** Draw a single settled frame per change instead of animating (reduced motion, idle previews). */
  still?: boolean;
  /** Cross-fade progress changes between settled frames when motion is allowed. */
  transitionStillFrames?: boolean;
  /**
   * Draw nothing and stop the loop while the canvas is hidden (e.g. behind an
   * embedded roleplay). The scene keeps its state and resumes where it was.
   */
  paused?: boolean;
  /** Send taps anywhere on the page into the scene as ripples. */
  interactive?: boolean;
  /** Start past the scene's entrance (taking over from the same theme on the previous screen). */
  skipIntro?: boolean;
  /** Quality-only remount: ignore events already consumed by the previous scene. */
  suppressInitialEvents?: boolean;
  onStep?: (anchor: SceneAnchor | null) => void;
  onFinale?: (anchor: SceneAnchor | null) => void;
  /** Called every animated frame with the burst anchor, so overlays can track it. */
  onAnchor?: (anchor: SceneAnchor | null) => void;
  /**
   * Called once the canvas shows what it is going to show: the first frame is
   * drawn, or the scene can't run and the static swatch behind it stays.
   */
  onReady?: () => void;
  /** Ask the scene for its lighter particle counts (phones and low-core devices get them anyway). */
  lowQuality?: boolean;
  /** Upper bound for the pixel ratio (e.g. 1 for still frames under software rendering). */
  maxPixelRatio?: number;
  /**
   * How this canvas adapts to a slow device. Both modes lower the resolution
   * on sustained slow frames and switch to still frames when even the lowest
   * resolution can't keep up. `session` (learner backdrops) also remembers the
   * resolution and the slow device for the rest of the tab, so the next
   * module starts there; `local` (small previews) only reads that memory.
   */
  performanceMemory?: 'session' | 'local';
  /** May frames go still on their own when they stay slow? Off for the QA `animated` override. */
  allowStillFallback?: boolean;
  /** Frames went still because the device couldn't keep up. */
  onPerformanceFallback?: (report: PerformanceFallbackReport) => void;
  className?: string;
}

const prefersLowQuality = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  const cores = typeof navigator !== 'undefined' ? navigator.hardwareConcurrency : undefined;
  return window.innerWidth < COMPACT_BREAKPOINT || (typeof cores === 'number' && cores <= 2);
};

/**
 * Hosts one animated flow theme on a canvas that fills its positioned parent.
 * The scene module loads on demand; the loop pauses in background tabs, drops
 * to 30fps once the motion settles (15fps once it has idled a while) and
 * adapts to slow devices: it times every drawn frame (see `FrameBudget` in
 * `renderBudget.ts`), drops the resolution straight to what should fit, and
 * holds hopeless devices to still frames. A scene that throws is switched off
 * for good, leaving the theme's static swatch behind the canvas.
 */
export const FlowThemeCanvas: React.FC<FlowThemeCanvasProps> = ({
  theme,
  progress,
  themeContext,
  still = false,
  transitionStillFrames = false,
  paused = false,
  interactive = false,
  skipIntro = false,
  suppressInitialEvents = false,
  onStep,
  onFinale,
  onAnchor,
  onReady,
  lowQuality = false,
  maxPixelRatio = MAX_PIXEL_RATIO,
  performanceMemory = 'local',
  allowStillFallback = true,
  onPerformanceFallback,
  className,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [factory, setFactory] = useState<SceneFactory | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [fallback, setFallback] = useState<'sustained_slow' | null>(null);
  const callbacksRef = useRef({ onStep, onFinale, onAnchor, onReady, onPerformanceFallback });
  callbacksRef.current = { onStep, onFinale, onAnchor, onReady, onPerformanceFallback };
  const themeRef = useRef(theme);
  themeRef.current = theme;
  // Read when a scene starts; the policy behind them is fixed per mount.
  const settingsRef = useRef({ lowQuality, maxPixelRatio, performanceMemory, allowStillFallback });
  settingsRef.current = { lowQuality, maxPixelRatio, performanceMemory, allowStillFallback };
  // A quality change remounts the scene at the existing position. Events already
  // present at mount belong to the previous scene and must not celebrate again.
  const initialEventIds = useRef(new Set(suppressInitialEvents ? themeContext?.events.map(event => event.id) ?? [] : []));
  const canvasContext = useMemo(() => themeContext && initialEventIds.current.size
    ? Object.freeze({ ...themeContext, events: Object.freeze(themeContext.events.filter(event => !initialEventIds.current.has(event.id))) })
    : themeContext, [themeContext]);
  const dynamicRef = useRef(canvasContext);
  dynamicRef.current = canvasContext;
  const progressRef = useRef(progress);
  progressRef.current = progress;
  const stillRef = useRef(still);
  stillRef.current = still;
  const transitionStillFramesRef = useRef(transitionStillFrames);
  transitionStillFramesRef.current = transitionStillFrames;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const skipIntroRef = useRef(skipIntro);
  // A route handoff can be captured in layout cleanup after this canvas first
  // renders. Consume the latest choice when the async scene creates its driver.
  skipIntroRef.current = skipIntro;
  const requestDrawRef = useRef<() => void>(() => undefined);
  const touchRef = useRef<(x: number, y: number) => void>(() => undefined);
  const readyReportedRef = useRef(false);
  const reportReadyRef = useRef(() => {
    if (readyReportedRef.current) {
      return;
    }
    readyReportedRef.current = true;
    setIsReady(true);
    callbacksRef.current.onReady?.();
  });

  useEffect(() => {
    let active = true;
    setFactory(null);
    setFallback(null);
    readyReportedRef.current = false;
    setIsReady(false);
    if (!theme.loadScene) {
      reportReadyRef.current();
      return undefined;
    }
    theme.loadScene()
      .then((loaded) => {
        if (active) {
          setFactory(() => loaded);
        }
      })
      .catch((error) => {
        console.warn('Unable to load the flow theme scene', error);
        if (active) {
          // The swatch behind the canvas is all this theme will show.
          reportReadyRef.current();
        }
      });
    return () => {
      active = false;
    };
  }, [theme]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!factory || !canvas) {
      return undefined;
    }
    let context: CanvasRenderingContext2D | null = null;
    try {
      context = canvas.getContext('2d', { alpha: false });
    } catch {
      context = null;
    }
    if (!context) {
      reportReadyRef.current();
      return undefined;
    }
    const ctx = context;

    const settings = settingsRef.current;
    const softwareFriendly = Boolean(themeRef.current.softwareFriendly);
    let driver: ThemeDriver;
    try {
      const quality = settings.lowQuality || prefersLowQuality() ? 'low' : 'high';
      driver = new ThemeDriver(factory({ quality }), {
        onStep: (anchor) => callbacksRef.current.onStep?.(anchor),
        onFinale: (anchor) => callbacksRef.current.onFinale?.(anchor),
      });
    } catch (error) {
      canvas.style.visibility = 'hidden';
      console.warn('Flow theme scene failed to start; falling back to the static backdrop', error);
      reportReadyRef.current();
      return undefined;
    }
    let targetProgress = { ...progressRef.current };
    let renderedProgress: FlowThemeProgress | null = null;
    driver.setTarget(targetProgress);
    if (skipIntroRef.current) {
      driver.skipIntro();
    }

    const mountedAt = performance.now();
    let width = 0;
    let height = 0;
    let pixelRatio = 1;
    // What this canvas learned, in backing-store pixels, so a viewport resize
    // keeps the level. Friendly themes are cheap enough to start at full
    // resolution rather than inherit a heavy theme's budget, and a canvas that
    // starts still draws one frame per step, so it isn't bound by it either.
    let pixelBudget = softwareFriendly || stillRef.current ? null : readRenderMemory().pixelBudget ?? null;
    let budget: FrameBudget | null = null;
    // A drawn frame's cost is only known at the next animation frame: the gap
    // to it covers the scene's JS plus the raster at frame flush (where a
    // software canvas spends most of its time) and compositing. It is taken
    // after every drawn frame, so settled and idle frames count too.
    let pendingCost: { at: number; renderMs: number } | null = null;
    let performanceStill = false;
    let frameId: number | null = null;
    let lastFrameAt = 0;
    let lastChangeAt = performance.now();
    let settledSince: number | null = null;
    let resizeTimer: number | null = null;
    let fadeTimer: number | null = null;
    let fadeCanvas: HTMLCanvasElement | null = null;
    let failed = false;

    const isStill = () => stillRef.current || performanceStill;
    let previousStill = isStill();
    let previousTransitionStillFrames = transitionStillFramesRef.current;
    let skipNextStillTransition = false;

    const stop = () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
        frameId = null;
      }
      pendingCost = null;
    };

    // A broken scene must never take the flow down with it: stop drawing for
    // good and leave the theme's static swatch behind the canvas showing.
    const fail = (error: unknown) => {
      failed = true;
      stop();
      removeFade();
      canvas.style.visibility = 'hidden';
      console.warn('Flow theme scene failed; falling back to the static backdrop', error);
      reportReadyRef.current();
    };

    const applyPixelRatio = (ratio: number) => {
      pixelRatio = ratio;
      canvas.width = Math.round(width * pixelRatio);
      canvas.height = Math.round(height * pixelRatio);
      canvas.dataset.flowThemePixelRatio = pixelRatio.toFixed(2);
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = Math.max(1, rect.width);
      height = Math.max(1, rect.height);
      const bounds = fitPixelRatio({
        width,
        height,
        devicePixelRatio: window.devicePixelRatio || 1,
        pixelBudget,
        maxRatio: settings.maxPixelRatio,
      });
      if (budget) {
        budget.setBounds(bounds, performance.now());
      } else {
        budget = new FrameBudget(bounds, performance.now(), settings.allowStillFallback);
      }
      applyPixelRatio(bounds.ratio);
    };

    /** Draws one frame and returns how long the scene's JS took (ms). */
    const draw = (now: number, settle: boolean, maxDelta?: number) => {
      if (failed) {
        return 0;
      }
      let anchor: SceneAnchor | null = null;
      const startedAt = performance.now();
      try {
        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        driver.setThemeContext(dynamicRef.current);
        driver.render(ctx, width, height, width < COMPACT_BREAKPOINT, now / 1000, settle, maxDelta);
        renderedProgress = { ...targetProgress };
        if (!settle) {
          anchor = driver.anchor();
        }
      } catch (error) {
        fail(error);
        return 0;
      }
      const renderMs = performance.now() - startedAt;
      reportReadyRef.current();
      if (!settle) {
        callbacksRef.current.onAnchor?.(anchor);
      }
      return renderMs;
    };

    const removeFade = () => {
      if (fadeTimer !== null) {
        window.clearTimeout(fadeTimer);
        fadeTimer = null;
      }
      fadeCanvas?.remove();
      fadeCanvas = null;
    };

    // Lays a copy of the visible frame over the canvas and fades it out,
    // so switching settled frames cross-fades instead of jumping. It costs
    // one blit and a compositor opacity transition.
    const crossFadeFromCurrentFrame = () => {
      try {
        if (!canvas.parentElement || !canvas.width || !canvas.height) {
          return;
        }
        const copy = document.createElement('canvas');
        copy.width = canvas.width;
        copy.height = canvas.height;
        const copyContext = copy.getContext('2d');
        if (!copyContext || typeof copyContext.drawImage !== 'function') {
          return;
        }
        copyContext.drawImage(canvas, 0, 0);
        // During a rapid change, preserve the partly faded previous frame too,
        // rather than jumping to the underlying target before the next fade.
        if (fadeCanvas) {
          const opacity = Number.parseFloat(window.getComputedStyle(fadeCanvas).opacity);
          if (Number.isFinite(opacity) && opacity > 0) {
            copyContext.globalAlpha = Math.min(1, opacity);
            copyContext.drawImage(fadeCanvas, 0, 0, copy.width, copy.height);
            copyContext.globalAlpha = 1;
          }
        }
        copy.setAttribute('aria-hidden', 'true');
        copy.setAttribute('data-testid', 'flow-theme-crossfade');
        Object.assign(copy.style, {
          position: 'absolute',
          inset: '0',
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          opacity: '1',
          transition: `opacity ${STILL_CROSSFADE_MS}ms ease-out`,
        });
        removeFade();
        canvas.insertAdjacentElement('afterend', copy);
        fadeCanvas = copy;
        // Commit the opaque start (forces style) so the opacity change transitions.
        copy.getBoundingClientRect();
        copy.style.opacity = '0';
        fadeTimer = window.setTimeout(removeFade, STILL_CROSSFADE_MS + 100);
      } catch {
        removeFade();
      }
    };

    // Even the lowest resolution can't keep up: draw still frames from now on
    // (one per step, as for reduced motion) and remember it for the tab. The
    // still frame is drawn in the same task as the last animated one, so there
    // is never a blank frame in between.
    const fallBackToStill = (stats: FrameCostStats, now: number) => {
      performanceStill = true;
      previousStill = true;
      stop();
      if (settings.performanceMemory === 'session') {
        rememberSlowDevice(softwareFriendly ? 'all' : 'heavy');
      }
      crossFadeFromCurrentFrame();
      draw(now, true);
      setFallback('sustained_slow');
      callbacksRef.current.onPerformanceFallback?.({
        theme: themeRef.current.id,
        reason: 'sustained_slow',
        pixelRatio,
        p50Ms: stats.p50,
        p95Ms: stats.p95,
        elapsedSeconds: Math.max(0, now - mountedAt) / 1000,
      });
    };

    /** Feeds one frame cost to the budget and applies its decision. */
    const measure = (now: number, cost: number): 'none' | 'resized' | 'still' => {
      if (!budget) {
        return 'none';
      }
      const decision = budget.record(now, cost);
      if (decision.type === 'resize') {
        applyPixelRatio(decision.ratio);
        pixelBudget = backingPixels(width, height, decision.ratio);
        if (settings.performanceMemory === 'session' && !softwareFriendly) {
          rememberPixelBudget(pixelBudget);
        }
        return 'resized';
      }
      if (decision.type === 'still') {
        fallBackToStill(decision.stats, now);
        return 'still';
      }
      return 'none';
    };

    const loop = (now: number) => {
      frameId = window.requestAnimationFrame(loop);
      let resized = false;
      if (pendingCost) {
        // Never below the scene's own JS time, in case the timestamps are coarse.
        const gap = now - pendingCost.at;
        const renderMs = pendingCost.renderMs;
        pendingCost = null;
        // A long scheduling gap with no corresponding JS cost can be an
        // occluded window, suspension or unrelated work, not a slow canvas.
        // Do not permanently learn its one-second callback cadence as raster
        // cost. A scene whose own JS takes that long still counts normally.
        const suspended = gap > SUSPENDED_FRAME_GAP_MS && renderMs < SUSPENDED_FRAME_GAP_MS;
        if (suspended) {
          budget?.restart(now);
        }
        const outcome = suspended ? 'none' : measure(now, Math.max(gap, renderMs));
        if (outcome === 'still') {
          return;
        }
        resized = outcome === 'resized';
      }
      const elapsed = now - lastFrameAt;
      const sinceChange = now - lastChangeAt;
      const springsSettled = driver.isSettled();
      settledSince = springsSettled ? settledSince ?? now : null;
      const settled = springsSettled && sinceChange > SETTLED_AFTER_MS;
      const idle = settled
        && settledSince !== null
        && now - settledSince > IDLE_AFTER_MS
        && sinceChange > IDLE_AFTER_MS;
      const minFrameMs = idle ? IDLE_FRAME_MS : settled ? SETTLED_FRAME_MS : 0;
      // A resolution change cleared the canvas, so this frame is drawn even
      // when the settled frame cap would skip it.
      if (minFrameMs && elapsed < minFrameMs - 1 && !resized) {
        return;
      }
      lastFrameAt = now;
      const renderMs = draw(now, false, idle ? IDLE_MAX_DELTA_SECONDS : undefined);
      if (!failed) {
        pendingCost = { at: now, renderMs };
      }
    };

    const start = () => {
      if (frameId === null && !failed && !isStill() && !pausedRef.current && !document.hidden) {
        lastFrameAt = 0;
        pendingCost = null;
        // Time spent stopped (a background tab, a pause) says nothing about frame cost.
        budget?.restart(performance.now());
        frameId = window.requestAnimationFrame(loop);
      }
    };

    requestDrawRef.current = () => {
      if (failed) {
        removeFade();
        return;
      }
      lastChangeAt = performance.now();
      targetProgress = { ...progressRef.current };
      driver.setTarget(targetProgress);
      const motionChanged = previousStill !== isStill()
        || previousTransitionStillFrames !== transitionStillFramesRef.current;
      previousStill = isStill();
      previousTransitionStillFrames = transitionStillFramesRef.current;
      if (motionChanged || !transitionStillFramesRef.current) {
        removeFade();
      }
      if (pausedRef.current || document.hidden) {
        stop();
        removeFade();
        skipNextStillTransition = true;
      } else if (isStill()) {
        stop();
        const progressChanged = renderedProgress !== null && (
          renderedProgress.step !== targetProgress.step
          || renderedProgress.total !== targetProgress.total
          || renderedProgress.complete !== targetProgress.complete
        );
        if (transitionStillFramesRef.current && !motionChanged && !skipNextStillTransition && progressChanged) {
          crossFadeFromCurrentFrame();
        }
        draw(performance.now(), true);
        skipNextStillTransition = false;
      } else {
        skipNextStillTransition = false;
        start();
      }
    };

    touchRef.current = (x: number, y: number) => {
      if (failed || pausedRef.current || isStill()) {
        return;
      }
      try {
        driver.touch(x, y);
      } catch (error) {
        fail(error);
        return;
      }
      // A tap is interaction: back to the full frame rate for its ripple.
      lastChangeAt = performance.now();
      start();
    };

    resize();
    if (!pausedRef.current && !document.hidden) {
      draw(performance.now(), isStill());
      start();
    }

    const applyResize = () => {
      resizeTimer = null;
      const rect = canvas.getBoundingClientRect();
      if (Math.abs(rect.width - width) < 0.5 && Math.abs(rect.height - height) < 0.5) {
        return;
      }
      resize();
      if (pausedRef.current || document.hidden) {
        return;
      }
      // Resizing clears the opaque canvas to black and resize observers run after
      // this frame's animation callback, so repaint now to avoid a dark flash.
      draw(performance.now(), isStill());
    };
    const handleResize = () => {
      if (width <= 1 || height <= 1) {
        // First real layout: size immediately rather than stretching a 1px frame.
        applyResize();
        return;
      }
      if (resizeTimer !== null) {
        window.clearTimeout(resizeTimer);
      }
      resizeTimer = window.setTimeout(applyResize, RESIZE_SETTLE_MS);
    };
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(handleResize);
      observer.observe(canvas);
    } else {
      window.addEventListener('resize', handleResize);
    }

    const handleVisibility = () => {
      if (document.hidden) {
        stop();
        removeFade();
        skipNextStillTransition = true;
      } else {
        requestDrawRef.current();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stop();
      removeFade();
      if (resizeTimer !== null) {
        window.clearTimeout(resizeTimer);
      }
      try {
        driver.dispose();
      } catch (error) {
        console.warn('Flow theme scene failed to clean up', error);
      }
      observer?.disconnect();
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibility);
      requestDrawRef.current = () => undefined;
      touchRef.current = () => undefined;
    };
  }, [factory]);

  useEffect(() => {
    requestDrawRef.current();
  }, [progress.step, progress.total, progress.complete, still, transitionStillFrames, paused, themeContext]);

  useEffect(() => {
    if (!interactive || still || paused || fallback) {
      return undefined;
    }
    const handlePointerDown = (event: PointerEvent) => {
      // Only taps on the backdrop itself, not on the question card or controls.
      if (event.target instanceof Element && event.target.closest(INTERACTIVE_SELECTOR)) {
        return;
      }
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }
      const rect = canvas.getBoundingClientRect();
      touchRef.current(event.clientX - rect.left, event.clientY - rect.top);
    };
    window.addEventListener('pointerdown', handlePointerDown, { passive: true });
    return () => window.removeEventListener('pointerdown', handlePointerDown);
  }, [interactive, still, paused, fallback]);

  return (
    <canvas
      ref={canvasRef}
      className={className ?? 'absolute inset-0 h-full w-full'}
      data-testid="flow-theme-canvas"
      data-flow-theme-ready={isReady ? 'true' : 'false'}
      data-flow-theme-paused={paused ? 'true' : undefined}
      data-flow-theme-still={still || fallback ? 'true' : undefined}
      data-flow-theme-fallback={fallback ?? undefined}
      aria-hidden="true"
    />
  );
};
